import { forwardRef, useContext, useState } from 'react';
import { Button } from '@accure/ui/components/button/button-catalyst';
import { ChevronDown, ChevronUp } from 'lucide-react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AlertStatus } from '@/entities/alert-status';
import { useMe } from '@/hooks/use-me';
import { IPCSRepository } from '@/pcs/domain/repositories/pcs.repository';
import { cls } from '@/utils/cls';
import { formatPower } from '@/utils/format/battery';
import { deviceType } from '@/utils/format/device-type';
import { formatNumber } from '@/utils/format/number';

import { AlertStatusBadge } from '../components/AlertStatusBadge';
import { PCSOverviewContext } from '../pages/pcs-overview';
import { GetAllPCS } from '../queries/get-all-pcs';

import { PlotSection } from './plots/plot-section';
import { OverviewTableSkeleton } from './skeletons/OverviewTableSkeleton';

function formatPercentageNumber(
  perc: number,
  minimumFractionDigits: number = 1,
  maximumFractionDigits: number = minimumFractionDigits,
) {
  return (
    <span className="whitespace-nowrap">
      {formatNumber(perc, minimumFractionDigits, maximumFractionDigits)} %
    </span>
  );
}

export type OverviewTableProps = {
  filters: IPCSRepository.Filters;
};
export const OverviewTable = forwardRef<HTMLDivElement, OverviewTableProps>(
  function ({ filters }, ref) {
    const me = useMe();
    const asset = useContext(PCSOverviewContext);

    // State: whether the table is expanded to full height or constrained with its own scroll
    const [expanded, setExpanded] = useState(false);

    const pcsResult = GetAllPCS.useQuery({
      filters,
      assetFullFrontendId: asset!.fullFrontendId,
    });

    if (pcsResult.isLoading) {
      return <OverviewTableSkeleton />;
    }
    if (pcsResult.isError) return <div>Error loading PCS overview</div>;

    // ------------------------------------------------------------------
    // Determine if **any** PCS has power‑capability data; otherwise hide
    // the column entirely for a cleaner UI.
    const hasPowerCapabilityData = pcsResult.data.some(
      (pcs) =>
        pcs.powerCapabilityCHAAvgLast7d !== undefined ||
        pcs.powerCapabilityDCHAvgLast7d !== undefined,
    );

    // Determine if any PCS has type or inverterType data
    const hasPcsTypeData = pcsResult.data.some(
      (pcs) => pcs.type !== undefined && pcs.type !== null && pcs.type !== '',
    );

    const hasInverterTypeData = pcsResult.data.some(
      (pcs) =>
        pcs.inverterType !== undefined &&
        pcs.inverterType !== null &&
        pcs.inverterType !== '',
    );
    // ------------------------------------------------------------------

    function renderAggregatedValues<T extends any[]>(
      formatterFunction: (value: number, ...args: T) => React.ReactNode,
      value?: number,
      status: AlertStatus = AlertStatus.OK,
      ...args: T
    ) {
      if (value === undefined) {
        // if no data, show placeholder
        return <span className="whitespace-pre">{'     -     '}</span>;
      }
      const formatted = formatterFunction(value, ...args);
      return (
        <span className="whitespace-pre">
          {status !== AlertStatus.OK ? (
            <span className="font-bold underline">{formatted}</span>
          ) : (
            formatted
          )}
        </span>
      );
    }

    function renderAggregatedValuesChargeDischarge<T extends any[]>(
      formatterFunction: (value: number, ...args: T) => React.ReactNode,
      valueCHA?: number,
      valueDCH?: number,
      status: AlertStatus = AlertStatus.OK,
      ...args: T
    ) {
      return (
        <span className="whitespace-nowrap">
          {renderAggregatedValues(formatterFunction, valueCHA, status, ...args)}{' '}
          /{' '}
          {renderAggregatedValues(formatterFunction, valueDCH, status, ...args)}
        </span>
      );
    }

    return (
      <PlotSection sectionName="Overview Table">
        {/* Scrollable container when not expanded */}
        <div
          ref={ref}
          className={cls(
            'relative w-full',
            !expanded ? 'max-h-[50vh] overflow-y-auto' : '',
          )}
        >
          <Table
            striped
            dense
            bleed
            ref={ref}
            className="w-full table-auto overflow-auto"
          >
            <TableHead className="sticky top-0 bg-gray-100">
              <TableRow>
                <TableHeader className="text-center">PCS ID</TableHeader>
                <TableHeader narrow className="text-center">
                  Status
                </TableHeader>
                <TableHeader narrow className="text-center">
                  PCS Efficiency <sup>*/**</sup>
                </TableHeader>
                {hasPowerCapabilityData && (
                  <TableHeader narrow className="text-center">
                    Power Capability <sup>*/**</sup>
                  </TableHeader>
                )}
                <TableHeader narrow className="text-center">
                  Power Availability <sup>*</sup>
                </TableHeader>
                {hasPcsTypeData && (
                  <TableHeader wide className="text-center">
                    PCS Type
                  </TableHeader>
                )}
                {hasInverterTypeData && (
                  <TableHeader wide className="text-center">
                    Inverter Type
                  </TableHeader>
                )}
                <TableHeader narrow className="text-center">
                  Max Power <sup>**</sup>
                </TableHeader>
              </TableRow>
            </TableHead>

            <TableBody>
              {pcsResult.data.map((pcs_entry) => (
                <TableRow key={pcs_entry.id} className="hover:bg-gray-50">
                  <TableCell wide justify="left">
                    {pcs_entry.fullFrontendId}
                  </TableCell>
                  <TableCell justify="right">
                    <div className="flex justify-end">
                      <AlertStatusBadge status={pcs_entry.status} />
                    </div>
                  </TableCell>
                  <TableCell narrow justify="center">
                    {renderAggregatedValuesChargeDischarge(
                      formatPercentageNumber,
                      pcs_entry.pcsEfficiencyCHAAvgLast7d,
                      pcs_entry.pcsEfficiencyDCHAvgLast7d,
                      pcs_entry.algorithmUID === 'pcs_efficiency'
                        ? pcs_entry.status
                        : AlertStatus.OK,
                    )}
                  </TableCell>
                  {hasPowerCapabilityData && (
                    <TableCell narrow justify="center">
                      {renderAggregatedValuesChargeDischarge(
                        formatPower,
                        pcs_entry.powerCapabilityCHAAvgLast7d,
                        pcs_entry.powerCapabilityDCHAvgLast7d,
                        pcs_entry.algorithmUID === 'power_capability'
                          ? pcs_entry.status
                          : AlertStatus.OK,
                        me.config.powerUnit,
                      )}
                    </TableCell>
                  )}
                  <TableCell narrow justify="center">
                    {renderAggregatedValues(
                      formatPercentageNumber,
                      pcs_entry.powerAvailabilityAvgLast7d,
                      pcs_entry.algorithmUID === 'power_availability'
                        ? pcs_entry.status
                        : AlertStatus.OK,
                    )}
                  </TableCell>
                  {hasPcsTypeData && (
                    <TableCell wide justify="center">
                      {deviceType(pcs_entry.type) ?? '-'}
                    </TableCell>
                  )}
                  {hasInverterTypeData && (
                    <TableCell wide justify="center">
                      {deviceType(pcs_entry.inverterType)}
                    </TableCell>
                  )}
                  <TableCell narrow justify="center">
                    {renderAggregatedValuesChargeDischarge(
                      formatPower,
                      pcs_entry.powerMaxCha,
                      pcs_entry.powerMaxDch,
                      AlertStatus.OK,
                      me.config.powerUnit,
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {/* Fade effect at the bottom when not expanded - sticky positioned */}
          {!expanded && pcsResult.data.length > 10 && (
            <div className="pointer-events-none sticky inset-x-0 bottom-0 -mt-32 h-32 bg-gradient-to-t from-white to-transparent" />
          )}
        </div>
        {/* Expand / Collapse toggle */}
        {pcsResult.data.length > 10 && (
          <div
            className={`flex justify-center text-center ${!expanded ? 'relative z-10 -mt-8' : 'mt-3'}`}
          >
            <Button
              type="button"
              plain
              onClick={() => setExpanded((prev) => !prev)}
              className="flex items-center gap-1.5 rounded-full border border-gray-100 bg-white/80 px-4 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition-all duration-150 hover:text-primary-600 hover:shadow"
            >
              {expanded ? <ChevronUp /> : <ChevronDown />}
              {expanded ? 'Show less' : `Show all (${pcsResult.data.length})`}
            </Button>
          </div>
        )}
        {/* Fine Print Section */}
        <ul className="ml-4 mt-2 list-none text-xs text-gray-500">
          <li className="flex gap-2">
            <span className="w-4 text-right text-gray-500">*</span>
            <span>
              Efficiency and Power values are averaged over the last 7 days.
            </span>
          </li>
          <li className="flex gap-2">
            <span className="w-4 text-right text-gray-500">**</span>
            <span>Values shown for charge/discharge.</span>
          </li>
        </ul>
      </PlotSection>
    );
  },
);
OverviewTable.displayName = 'OverviewTable';
